import React, { useEffect, useRef, useState } from "react";
import MetricsService from "../../api/MetricsService";
import { useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import moment, { Moment } from "moment";
import { useIdleTimer } from "react-idle-timer";
import { getMetricData, getRecipientData } from "../../../redux/selectors";
import { useLocation, useParams } from "react-router-dom";
import AppraisalsService from "../../api/AppraisalsService";

interface ContextProps {
  sessionId: string | undefined;
  disableMetrics: boolean;
}

const defaultValue = {
  sessionId: undefined,
  disableMetrics: false,
};

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export const MetricContext = React.createContext<ContextProps>(defaultValue);
interface PreviewProps {
  proposalId: string;
}

const PreviewMetricHOC = ({ children = <></> }) => {
  const { id, officeId } = useSelector(getMetricData);
  const contacts = useSelector(getRecipientData);
  let recipient = {
    firstName: undefined,
    lastName: undefined,
  };
  let { proposalId } = useParams<PreviewProps>();
  const flkLink = proposalId.split("-")[1];
  if (flkLink) {
    recipient = contacts.find(
      (c: { flkUrl: string }) => c.flkUrl === atob(flkLink),
    );
  }

  const [sessionStart, setSessionStart] = useState<Moment>();
  const [sessionId, setSessionId] = useState<string>();
  const intervalID = useRef<any>();

  // let sessionStart = useRef<Moment>();
  // let sessionId = useRef<string>();
  // let intervalID = useRef<number>();
  let query = useQuery();

  const [sentSms, setSentSms] = useState(false);

  const disableByQuery = query.get("disableMetrics") === "true";
  const disableByLS = localStorage.getItem("preview") === "1";
  const disableMetrics = disableByQuery || disableByLS || !id;

  const handleOnIdle = (event: any) => {
    if (disableMetrics) return;
    console.log("user is idle", event);
    clearInterval(intervalID.current);
  };

  const handleOnActive = (event: any) => {
    if (disableMetrics) return;
    console.log("user is active", event);
    console.log("time remaining", getRemainingTime());
  };

  const handleOnAction = (event: any) => {
    if (disableMetrics) return;
    console.log("user did something", event);
  };

  const { getRemainingTime } = useIdleTimer({
    timeout: 1000 * 60 * 2,
    onIdle: handleOnIdle,
    onActive: handleOnActive,
    onAction: handleOnAction,
    debounce: 500,
  });

  useEffect(() => {
    if (!disableMetrics && officeId && id && !sentSms) {
      setSentSms(true);
      AppraisalsService.smsToAgent(id)
        .then(() => console.log("SMS sent to agent"))
        .catch(err => console.error("Error sending SMS:", err));
    }
  }, [id, officeId, sentSms, disableMetrics]);

  const postMetric = () => {
    try {
      console.log("SESSION", sessionId);
      const now = moment();
      const duration = sessionStart
        ? moment.duration(now.diff(sessionStart)).as("seconds")
        : 0;

      MetricsService.postMetric({
        appraisal: id,
        office: officeId,
        sessionId: sessionId || "",
        type: "session",
        recipient: `${recipient?.firstName || ""} ${recipient?.lastName ||
          ""}`.trim(),
        duration,
      })
        .then(() => console.log("Metric posted successfully"))
        .catch(err => console.error("Error posting metric:", err));
    } catch (error) {
      console.error("Unexpected error in postMetric:", error);
    }
  };

  useEffect(() => {
    if (!sessionId) setSessionId(uuidv4());
    if (!sessionStart) setSessionStart(moment());
    if (!disableMetrics) {
      intervalID.current = setInterval(() => {
        postMetric();
      }, 5000);
    }

    return () => {
      if (intervalID.current) clearInterval(intervalID.current);
    };
  }, [disableMetrics, sessionId, sessionStart]);

  return (
    <MetricContext.Provider
      value={{
        sessionId,
        disableMetrics,
      }}
    >
      {children}
    </MetricContext.Provider>
  );
};

export default PreviewMetricHOC;
